/**
 * Lightweight i18n: Chinese source text is the lookup key.
 */

import { useMemo } from 'react';

import { useLanguageStore, type Lang } from '@/store/languageStore';
import { en } from './en';

export type TranslateVars = Record<string, string | number>;
export type Translate = (zh: string, vars?: TranslateVars) => string;

const interpolate = (text: string, vars?: TranslateVars): string => {
  if (!vars) return text;
  return text.replace(/\{(\w+)\}/g, (match, key: string) =>
    Object.prototype.hasOwnProperty.call(vars, key) ? String(vars[key]) : match
  );
};

export const translate = (lang: Lang, zh: string, vars?: TranslateVars): string => {
  let text = zh;
  if (lang === 'en') {
    const translated = en[zh];
    if (translated === undefined) {
      if (import.meta.env.DEV && /[一-龥]/.test(zh)) {
        console.warn(`[i18n] missing English translation: ${zh}`);
      }
    } else {
      text = translated;
    }
  }
  return interpolate(text, vars);
};

export const makeT = (lang: Lang): Translate => (zh, vars) => translate(lang, zh, vars);

/** Non-React callers (api/utils/message builders) use the current store value. */
export const t: Translate = (zh, vars) => translate(useLanguageStore.getState().lang, zh, vars);

/** Reactive translate function; re-renders the component when the language changes. */
export const useT = (): { t: Translate; lang: Lang } => {
  const lang = useLanguageStore((state) => state.lang);
  const translateForLang = useMemo(() => makeT(lang), [lang]);
  return { t: translateForLang, lang };
};
